/**
 * @file
 * @fileOverview  This file contains the implementation of the console component for A-Frame.
 * @version 1.0.0
 * @author Parker Eli Bidigare
 * @date 2024-05-26
 */

import CommandParser from './AframeComponentUtils/commandParser.js';
import addConsoleCommands from './AframeComponentUtils/ConsoleCommands.js';
import MemoryUsageMonitor from './AframeComponentUtils/consoleMemoryUsage.js';
//import { Spector } from 'spectorjs';

var parser = new CommandParser();
var memory = new MemoryUsageMonitor();

addConsoleCommands(parser);

AFRAME.registerComponent('console', {
  schema: {
    styles: { type: 'string', default: '' },
    customTabs: {
      type: 'array',
      default: []
    }
  },

  init: function() {
    this.consoleEl = document.createElement('div');
    this.consoleEl.id = 'aframe-console';
    this.consoleEl.style.display = 'none';
    document.body.appendChild(this.consoleEl);

    this.createConsoleContent();
    this.applyStyles();
    this.addEventListeners();
    this.setupDraggable();
    this.setupResizable();
    this.interceptConsole();
    
    setTimeout(() => {
      this.initializeTabs();
    }, 0);

    this.selectedEntity = null;
    this.setupEntitySelection();
    //this.spector = new Spector();
    this.isCapturing = false;
  },

  createConsoleContent: function() {
    const defaultTabs = [
      { name: 'Command', id: 'command' },
      { name: 'Console', id: 'console-log' },
      { name: 'A-Frame Info', id: 'info' },
      { name: 'Event Listeners', id: 'events' },
      { name: 'WebGL Debug', id: 'webgl-debug' },
    ];


    const allTabs = [...defaultTabs];

    const tabButtons = allTabs.map(tab => 
      `<button class="tab-button" data-tab="${tab.id}">${tab.name}</button>`
    ).join('');

    const tabContents = allTabs.map(tab => 
      `<div id="${tab.id}-tab" class="tab-content"></div>`
    ).join('');

    this.consoleEl.innerHTML = `
      <div id="console-header">
        <div id="console-tabs">${tabButtons}</div>
        <button id="close-console">&times;</button>
      </div>
      <div id="console-content">${tabContents}</div>
      <div id="resize-handle"></div>
    `;

    this.consoleEl.querySelector('#command-tab').innerHTML = `
      <div id="command-output"></div>
      <div id="command-input-container">
        <input type="text" id="command-input" placeholder="Enter command...">
      </div>
    `;
    this.consoleEl.querySelector('#console-log-tab').innerHTML = `
      <div id="console-log-output"></div>
    `;
    this.consoleEl.querySelector('#info-tab').innerHTML = `
      <div id="aframe-info"></div><br/>
      <div id="memory-info"></div><br/>
    `;
    this.consoleEl.querySelector('#events-tab').innerHTML = `
      <ul id="event-listeners-list"></ul>
    `;
    /*this.consoleEl.querySelector('#webgl-debug-tab').innerHTML = `
      <div id="spector-output"></div>
      <button id="start-capture">Start Capture</button>
      <button id="stop-capture">Stop Capture</button>
    `;*/
  },

  applyStyles: function() {
    const defaultStyles = `
      #aframe-console {
        position: fixed;
        top: 10px;
        left: 10px;
        width: 300px;
        height: 400px;
        background-color: rgba(0, 0, 0, 0.8);
        color: #fff;
        font-family: monospace;
        z-index: 9999;
        border-radius: 5px;
        overflow: hidden;
        resize: both;
      }
      #console-header {
        display: flex;
        justify-content: space-between;
        padding: 5px;
        background-color: #333;
        cursor: move;
      }
      #console-tabs {
        display: flex;
        flex-wrap: wrap;
      }
      .tab-button {
        background: none;
        border: none;
        color: #fff;
        padding: 5px 10px;
        cursor: pointer;
        margin: 2px;
      }
      .tab-button.active {
        background-color: #555;
      }
      #close-console {
        background: none;
        border: none;
        color: #fff;
        font-size: 20px;
        cursor: pointer;
      }
      #console-content {
        height: calc(100% - 40px);
        overflow-y: auto;
      }
      .tab-content {
        display: none;
        padding: 10px;
        height: 100%;
      }
      .tab-content.active {
        display: flex;
        flex-direction: column;
      }
      #command-output, #console-log-output {
        flex-grow: 1;
        overflow-y: auto;
        margin-bottom: 10px;
      }
      #command-input-container {
        width: 100%;
      }
      #command-input {
        width: 100%;
        background-color: #333;
        color: #fff;
        border: none;
        padding: 5px;
        box-sizing: border-box;
      }
      .error {
        color: #ff4444;
      }
      .warn {
        color: #ffaa00;
      }
      .log {
        color: #ffffff;
      }
      #resize-handle {
        position: absolute;
        right: 0;
        bottom: 0;
        width: 10px;
        height: 10px;
        cursor: se-resize;
      }
      #spector-output {
        height: calc(100% - 40px);
        overflow-y: auto;
        background-color: #222;
        padding: 10px;
        font-family: monospace;
      }
      #start-capture, #stop-capture {
        margin: 5px;
        padding: 5px 10px;
        background-color: #444;
        color: #fff;
        border: none;
        cursor: pointer;
      }
      #start-capture:hover, #stop-capture:hover {
        background-color: #555;
      }
    `;

    const style = document.createElement('style');
    style.textContent = defaultStyles + this.data.styles;
    document.head.appendChild(style);
  },

  addEventListeners: function() {
    document.addEventListener('keydown', this.toggleConsole.bind(this));
    document.addEventListener('touchstart', this.onTwoFingerTapToggle.bind(this), false);
    this.consoleEl.querySelector('#close-console').addEventListener('click', this.hideConsole.bind(this));
    this.consoleEl.querySelector('#command-input').addEventListener('keydown', this.handleCommand.bind(this));
    
    const tabButtons = this.consoleEl.querySelectorAll('.tab-button');
    tabButtons.forEach(button => {
      button.addEventListener('click', this.switchTab.bind(this));
    });
    //this.consoleEl.querySelector('#start-capture').addEventListener('click', this.startCapture.bind(this));
    //this.consoleEl.querySelector('#stop-capture').addEventListener('click', this.stopCapture.bind(this));
  },

  setupDraggable: function() {
    const header = this.consoleEl.querySelector('#console-header');
    let isDragging = false;
    let startX, startY, startLeft, startTop;

    const startDrag = (e) => {
      isDragging = true;
      startX = e.clientX;
      startY = e.clientY;
      startLeft = parseInt(window.getComputedStyle(this.consoleEl).left);
      startTop = parseInt(window.getComputedStyle(this.consoleEl).top);
      document.addEventListener('mousemove', drag);
      document.addEventListener('mouseup', stopDrag);
    };

    const drag = (e) => {
      if (isDragging) {
        const deltaX = e.clientX - startX;
        const deltaY = e.clientY - startY;
        this.consoleEl.style.left = `${startLeft + deltaX}px`;
        this.consoleEl.style.top = `${startTop + deltaY}px`;
      }
    };

    const stopDrag = () => {
      isDragging = false;
      document.removeEventListener('mousemove', drag);
      document.removeEventListener('mouseup', stopDrag);
    };

    header.addEventListener('mousedown', startDrag);
  },

  setupResizable: function() {
    const resizeHandle = this.consoleEl.querySelector('#resize-handle');
    let isResizing = false;
    let startX, startY, startWidth, startHeight;

    const startResize = (e) => {
      isResizing = true;
      startX = e.clientX;
      startY = e.clientY;
      startWidth = parseInt(window.getComputedStyle(this.consoleEl).width);
      startHeight = parseInt(window.getComputedStyle(this.consoleEl).height);
      document.addEventListener('mousemove', resize);
      document.addEventListener('mouseup', stopResize);
    };

    const resize = (e) => {
      if (isResizing) {
        const deltaX = e.clientX - startX;
        const deltaY = e.clientY - startY;
        this.consoleEl.style.width = `${startWidth + deltaX}px`;
        this.consoleEl.style.height = `${startHeight + deltaY}px`;
      }
    };

    const stopResize = () => {
      isResizing = false;
      document.removeEventListener('mousemove', resize);
      document.removeEventListener('mouseup', stopResize);
    };

    resizeHandle.addEventListener('mousedown', startResize);
  },

  interceptConsole: function() {
    const logOutput = this.consoleEl.querySelector('#console-log-output');
    const methods = ['log', 'warn', 'error'];
    
    methods.forEach(method => {
      const originalMethod = console[method];
      console[method] = (...args) => {
        try{
          originalMethod.apply(console, args);
          const message = args.map(arg => {
            if (typeof arg === 'object') {
              return JSON.stringify(arg);
            }
            return arg;
          }).join(' ');
          const logElement = document.createElement('div');
          logElement.classList.add(method);
          logElement.textContent = `[${method.toUpperCase()}] ${message}`;
          logOutput.appendChild(logElement);
          logOutput.scrollTop = logOutput.scrollHeight;
        } catch (error) {
          console.error(error);
        }
      };
    });
  },

  initializeTabs: function() {
    this.updateAFrameInfo();
    this.updateEventListeners();
    if(this.data.customTabs != null) {
      if(this.data.customTabs.length > 0) {
        this.data.customTabs.forEach(tab => {
        if (tab.init) tab.init(this.consoleEl.querySelector(`#${tab.id}-tab`));
        });
      }
    }
    this.switchTab({ target: this.consoleEl.querySelector('.tab-button') });
  },

  onTwoFingerTapToggle: function(event) {
    if (event.touches.length === 3) {
      event.preventDefault();
      this.consoleEl.style.display = this.consoleEl.style.display === 'none' ? 'block' : 'none';
      if (this.consoleEl.style.display === 'block') {
        this.updateAFrameInfo();
        this.updateEventListeners();
      }
    }
  },

  toggleConsole: function(event) {
    if (event.key === '`') {
      event.preventDefault();
      this.consoleEl.style.display = this.consoleEl.style.display === 'none' ? 'block' : 'none';
      if (this.consoleEl.style.display === 'block') {
        this.updateAFrameInfo();
        this.updateEventListeners();
      }
    }
  },

  hideConsole: function() {
    this.consoleEl.style.display = 'none';
  },

  setupEntitySelection: function() {
    const camera = document.querySelector('[camera]');
    if (!camera) {
      console.error('Camera not found. Entity selection may not work.');
      return;
    }
    
    camera.setAttribute('raycaster', {
      objects: '.selectable',
      far: 100
    });




    document.querySelectorAll('a-entity').forEach(entity => {
      entity.classList.add('selectable');
    });

    this.el.sceneEl.addEventListener('raycaster-intersection', (event) => {
      if (this.consoleEl.style.display === 'block') {
        const intersection = event.detail.intersections[0];
        if (intersection && intersection.object.el) {
          this.selectedEntity = intersection.object.el;
          const commandOutput = this.consoleEl.querySelector('#command-output');
          commandOutput.innerHTML += `<div>Selected entity: ${this.selectedEntity.id || 'unnamed entity'}</div>`;
          commandOutput.scrollTop = commandOutput.scrollHeight;
        }
      }
    });

    this.el.sceneEl.addEventListener('click', () => {
      if (this.consoleEl.style.display === 'block') {
        const raycasterEl = document.querySelector('[raycaster]');
        if (raycasterEl) {
          raycasterEl.components.raycaster.checkIntersections();
        }
      }
    });
  },

  handleCommand: function(event) {
    if (event.key === 'Enter') {
      const command = event.target.value;
      const output = this.consoleEl.querySelector('#command-output');
      
      function runBuiltinCommands(input) {
        const result = parser.parse(input);
        if (result === null) {
          return parser.getLastError();
        } else {
          return result;
        }
      }

      try {
        if (command.trim().toLowerCase() === '/cls') {
          output.innerHTML = '';
        } else if (command.trim().toLowerCase() === '/help') {
          let data = parser.getRegisteredCommandsHTML();
          output.innerHTML += data;
        } else if (command.trim().toLowerCase().startsWith('/apply')) {
          if (!this.selectedEntity) {
            output.innerHTML += `<div>> ${command}</div><div class="error">Error: No entity selected. Click on an entity in the scene to select it.</div>`;
          } else {
            let results = runBuiltinCommands(command);
            output.innerHTML += `<div>> ${command}</div><div>${results}</div>`;
          }
        } else if (parser.isCommandRegistered(command)) {
          let results = runBuiltinCommands(command);
          output.innerHTML += `<div>> ${command}</div><div>${results}</div>`;
        } else {
          const result = eval(command);
          output.innerHTML += `<div>> ${command}</div><div>${result}</div>`;
        }
      } catch (error) {
        output.innerHTML += `<div>> ${command}</div><div class="error">${error}</div>`;
      }
      
      event.target.value = '';
      output.scrollTop = output.scrollHeight;
    }
  },

  switchTab: function(event) {
    const tabName = event.target.getAttribute('data-tab');
    
    this.consoleEl.querySelectorAll('.tab-button').forEach(btn => btn.classList.remove('active'));
    event.target.classList.add('active');
    
    this.consoleEl.querySelectorAll('.tab-content').forEach(content => content.classList.remove('active'));
    this.consoleEl.querySelector(`#${tabName}-tab`).classList.add('active');
    
    if (tabName === 'info') {
      this.updateAFrameInfo();
    } else if (tabName === 'events') {
      this.updateEventListeners();
    } else {
      if(this.data.customTabs != null) {
        const customTab = this.data.customTabs.find(tab => tab.id === tabName);
        if (customTab && customTab.update) {
          customTab.update(this.consoleEl.querySelector(`#${tabName}-tab`));
        }
      }
    }
  },

  updateAFrameInfo: function() {
    const infoEl = this.consoleEl.querySelector('#aframe-info');
    const cameraEl = document.querySelector('[camera]');
    
    if (!cameraEl || !cameraEl.object3D) {
      infoEl.innerHTML = '<p>Camera not found or not fully initialized.</p>';
      return;
    }

    const camera = cameraEl.object3D;
    const renderer = this.el.sceneEl.renderer;
    
    if (!renderer) {
      infoEl.innerHTML = '<p>Renderer not found or not fully initialized.</p>';
      return;
    }

    infoEl.innerHTML = `
      <h3>Camera Position: ${camera.position.toArray().map(n => n.toFixed(2)).join(', ')}</h3>
      <h3>Camera Rotation: ${camera.rotation.toArray().map(n => (n * 180 / Math.PI).toFixed(2)).join(', ')} (degrees)</h3>
      <h3>FPS: ${(1000 / this.el.sceneEl.time.delta).toFixed(2)}</h3>
      <h3>Drawcalls: ${renderer.info.render.calls}</h3>
      <h3>Triangles: ${renderer.info.render.triangles}</h3>
      <h3>Geometries: ${renderer.info.memory.geometries}</h3>
      <h3>Textures: ${renderer.info.memory.textures}</h3>
    `;
  },

  updateEventListeners: function() {
    const listEl = this.consoleEl.querySelector('#event-listeners-list');
    listEl.innerHTML = '';
    
    if (this.el.sceneEl && this.el.sceneEl.listeners) {
      const eventNames = Object.keys(this.el.sceneEl.listeners);
      eventNames.forEach(eventName => {
        const li = document.createElement('li');
        li.textContent = `${eventName}: ${this.el.sceneEl.listeners[eventName].length} listener(s)`;
        listEl.appendChild(li);
      });
    } else {
      listEl.innerHTML = '<li>No event listeners found or scene not fully initialized.</li>';
    }
  },

  /*startCapture: function() {
    if (!this.isCapturing) {
      this.isCapturing = true;
      this.spector.startCapture(this.el.sceneEl.canvas, 200);
      console.log('WebGL capture started');
    }
  },

  stopCapture: function() {
    if (this.isCapturing) {
      this.isCapturing = false;
      this.spector.stopCapture();
      console.log('WebGL capture stopped');
      this.displayCaptureResult();
    }
  },

  displayCaptureResult: function() {
    const outputEl = this.consoleEl.querySelector('#spector-output');
    outputEl.innerHTML = 'Processing capture data...';

    this.spector.onCapture.add((capture) => {
      let html = '<h3>WebGL Capture Results:</h3>';
      html += `<p>Total Commands: ${capture.commands.length}</p>`;
      html += '<h4>Command Summary:</h4><ul>';
      
      const commandCounts = {};
      capture.commands.forEach(cmd => {
        commandCounts[cmd.name] = (commandCounts[cmd.name] || 0) + 1;
      });

      for (const [cmdName, count] of Object.entries(commandCounts)) {
        html += `<li>${cmdName}: ${count}</li>`;
      }
      html += '</ul>';

      html += '<h4>Detailed Command List:</h4><ul>';
      capture.commands.slice(0, 100).forEach(cmd => {  // Limit to first 100 commands for performance
        html += `<li>${cmd.name} (${cmd.commandNumber})</li>`;
      });
      html += '</ul>';

      outputEl.innerHTML = html;
    });
  },*/

  tick: function() {
    if (this.consoleEl.style.display === 'block') {
      memory.visualizeMemoryUsage('memory-info');

      const activeTab = this.consoleEl.querySelector('.tab-content.active');
      if (activeTab) {
        const tabName = activeTab.id.replace('-tab', '');
        if (tabName === 'info') {
          this.updateAFrameInfo();
        } else if (tabName === 'events') {
          this.updateEventListeners();
        } else {
          if(this.data.customTabs != null) {
            const customTab = this.data.customTabs.find(tab => tab.id === tabName);
            if (customTab && customTab.update) {
              customTab.update(activeTab);
            }
          }
        }
      }
    }
  }
});

export const debugComponents = {
  console: AFRAME.components['console'],
};